<template>
    <themed-modal
        name="manage-leases"
        title="Manage Leases"
        size="xl"
        :click-to-close="false"
        custom-class="manage-leases-modal"
        @opened="handleModalOpened"
        @closed="handleClosed"
    >
        <!-- Toolbar -->
        <div class="manage-leases__toolbar">
            <div class="manage-leases__toolbar-left">
                <themed-select
                    v-if="sites.selectedSite.leases.length"
                    v-model="selectedLeaseId"
                    :options="leaseOptions"
                    @input="handleLeaseChange"
                />
                <themed-button
                    v-if="canCreateLease"
                    size="sm"
                    @click="createLease"
                >
                    <theme-icon icon="add" type="icons" classes="w-4 h-4 flex" />
                    <span>New Lease</span>
                </themed-button>
            </div>

            <div v-if="section != 'lease-info' && leases.selectedLease" class="manage-leases__dates">
                <div class="manage-leases__date-badge">
                    <div class="manage-leases__date-value">{{ leases.selectedLease.start_date | date }}</div>
                    <div class="manage-leases__date-label">Start Date</div>
                </div>
                <div class="manage-leases__date-badge">
                    <div class="manage-leases__date-value">{{ leases.selectedLease.end_date | date }}</div>
                    <div class="manage-leases__date-label">End Date</div>
                </div>
            </div>
        </div>

        <!-- Content with Sidebar -->
        <div class="manage-leases__content">
            <!-- Sidebar Navigation -->
            <div class="manage-leases__sidebar">
                <button
                    @click="section = 'lease-info'"
                    :class="{ 'manage-leases__nav-item--active': section === 'lease-info' }"
                    class="manage-leases__nav-item"
                >
                    Lease Info
                </button>
                <button
                    v-if="aLeaseIsSelected"
                    @click="section = 'units'"
                    :class="{ 'manage-leases__nav-item--active': section === 'units' }"
                    class="manage-leases__nav-item"
                >
                    Units
                </button>
                <button
                    v-if="aLeaseIsSelected"
                    @click="section = 'usage'"
                    :class="{ 'manage-leases__nav-item--active': section === 'usage' }"
                    class="manage-leases__nav-item"
                >
                    Usage
                </button>
                <button
                    v-if="aLeaseIsSelected"
                    @click="section = 'expenses'"
                    :class="{ 'manage-leases__nav-item--active': section === 'expenses' }"
                    class="manage-leases__nav-item"
                >
                    Expenses
                </button>
                <button
                    v-if="aLeaseIsSelected"
                    @click="section = 'key-dates'"
                    :class="{ 'manage-leases__nav-item--active': section === 'key-dates' }"
                    class="manage-leases__nav-item"
                >
                    Key Dates
                </button>
            </div>

            <!-- Main Content Area -->
            <div class="manage-leases__main">
                <div v-show="section === 'lease-info'" class="manage-leases__section">
                    <lease-info v-if="selectedLeaseId" :form-errors="errors" />
                    <div v-else class="manage-leases__empty">
                        <span class="manage-leases__empty-text">No Leases Found</span>
                        <themed-button
                            v-if="canCreateLease"
                            variant="default"
                            @click="createLease"
                        >
                            <theme-icon icon="add" type="icons" classes="w-4 h-4" />
                            <span>Create Lease</span>
                        </themed-button>
                    </div>
                </div>

                <div v-show="section === 'units'" class="manage-leases__section">
                    <lease-units :per-page="5" />
                </div>

                <div v-show="section === 'usage'" class="manage-leases__section">
                    <lease-usage />
                </div>

                <div v-show="section === 'expenses'" class="manage-leases__section">
                    <lease-expenses
                        :form-errors="errors"
                        @selectedExpenseChange="selectedExpenseId = $event"
                    />
                </div>

                <div v-show="section === 'key-dates'" class="manage-leases__section">
                    <lease-key-dates
                        :form-errors="errors"
                        @selectedKeyDateChange="selectedKeyDateId = $event"
                    />
                </div>
            </div>
        </div>

        <template #footer>
            <div class="modal-actions">
                <themed-button variant="secondary" @click="hideModal">
                    Close
                </themed-button>
                <themed-button
                    v-if="aLeaseIsSelected || selectedLeaseId === 'new'"
                    variant="default"
                    :disabled="loading"
                    @click="save"
                >
                    {{ loading ? 'Saving...' : 'Save' }}
                </themed-button>
            </div>
        </template>
    </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';
import LeaseInfo from './LeaseInfo.vue';
import LeaseUnits from './LeaseUnits.vue';
import LeaseUsage from './LeaseUsage.vue';
import LeaseExpenses from './LeaseExpenses.vue';
import LeaseKeyDates from './LeaseKeyDates.vue';

export default {
    name: 'ManageLeasesV1',
    
    components: {
        ThemedModal,
        ThemedButton,
        ThemedSelect,
        ThemeIcon,
        LeaseInfo,
        LeaseUnits,
        LeaseUsage,
        LeaseExpenses,
        LeaseKeyDates
    },

    props: {
        selectedSection: {
            type: String,
            default: 'lease-info'
        }
    },

    data() {
        return {
            section: this.selectedSection,
            selectedExpenseId: '',
            selectedKeyDateId: '',
            errors: {},
            loading: false
        };
    },

    computed: {
        ...mapState(['leases', 'sites']),

        canCreateLease() {
            return this.$gate.can('create', 'site');
        },

        selectedLeaseId: {
            get() {
                return this.leases.selectedLease ? this.leases.selectedLease.id : '';
            },
            set(leaseId) {
                this.leases.selectedLease = _.find(this.sites.selectedSite.leases, ['id', leaseId]);
            }
        },

        aLeaseIsSelected() {
            return this.selectedLeaseId !== 'new' && this.selectedLeaseId !== '';
        },

        leaseOptions() {
            return this.sites.selectedSite.leases.map(lease => ({
                value: lease.id,
                label: lease.name
            }));
        }
    },

    mounted() {
        this.setLeasedSquareFootage();

        this.$eventHub.$on('change-section', (section) => {
            this.section = section;
        });
    },

    beforeDestroy() {
        this.$eventHub.$off('change-section');
    },

    watch: {
        selectedSection(newSection) {
            this.section = newSection;
        },
        selectedLeaseId() {
            this.setLeasedSquareFootage();
        },

        selectedExpenseId() {
            if (this.selectedExpenseId !== 'new') {
                this.$emit('expenseSaved', this.selectedExpenseId);
            }
        },

        selectedKeyDateId() {
            if (this.selectedKeyDateId !== 'new') {
                this.$emit('keyDateSaved', this.selectedKeyDateId);
            }
        }
    },

    methods: {
        handleModalOpened() {
            // Sync section with prop when modal opens
            this.section = this.selectedSection;
            
            // If opening to a section that requires a lease (not lease-info), ensure a lease is selected
            const sectionsRequiringLease = ['units', 'usage', 'expenses', 'key-dates'];
            if (sectionsRequiringLease.includes(this.selectedSection)) {
                // If no lease is selected, select the first one
                if (!this.aLeaseIsSelected && this.sites.selectedSite.leases.length > 0) {
                    const firstLease = this.sites.selectedSite.leases[0];
                    this.leases.selectedLease = firstLease;
                }
            }
        },

        hideModal() {
            this.$modal.hide('manage-leases');
        },

        handleClosed() {
            this.errors = {};
            this.loading = false;
        },

        handleLeaseChange(leaseId) {
            this.$emit('selectedLeaseChange', parseInt(leaseId));
        },

        setLeasedSquareFootage() {
            if (this.leases.selectedLease) {
                this.leases.selectedLease.units = _.map(this.leases.selectedLease.units, (unit) => {
                    unit.leased_square_footage = unit.pivot.leased_square_footage;
                    return unit;
                });
            }
        },

        createLease() {
            this.sites.selectedSite.leases.push({
                id: 'new',
                site_id: this.sites.selectedSite.id,
                name: this.sites.selectedSite.leases.length === 0 ? 'Main Lease' : '',
                units: [],
                start_date: '',
                end_date: '',
                free_months: ''
            });

            this.selectedLeaseId = 'new';
            this.section = 'lease-info';
        },

        getSaveUrl() {
            let leaseId = this.leases.selectedLease.id;

            if (this.section === 'lease-info' || this.section === 'usage') {
                return leaseId === "new" ? '/leases' : '/leases/' + leaseId;
            } else if (this.section === 'units') {
                return '/leases/' + leaseId + '/units';
            } else if (this.section === 'expenses') {
                return this.selectedExpenseId === "new" ? '/expenses' : '/expenses/' + this.selectedExpenseId;
            } else if (this.section === 'key-dates') {
                return this.selectedKeyDateId === "new"
                    ? '/leases/' + leaseId + '/key-dates'
                    : '/leases/' + leaseId + '/key-dates/' + this.selectedKeyDateId;
            }
        },

        getSaveData() {
            if (this.section === 'lease-info' || this.section === 'usage') {
                this.leases.selectedLease._method = this.leases.selectedLease.id === "new" ? "POST" : "PATCH";
                return this.leases.selectedLease;
            } else if (this.section === 'units') {
                this.leases.selectedLease._method = "PATCH";
                return this.leases.selectedLease;
            } else if (this.section === 'expenses') {
                let expense = _.find(this.leases.selectedLease.expenses, ['id', this.selectedExpenseId]);
                expense._method = this.selectedExpenseId === "new" ? "POST" : "PATCH";
                expense.amount = Number.prototype.cleanseFloat(expense.amount_in_dollars) * 100;
                return expense;
            } else if (this.section === 'key-dates') {
                let keyDate = _.find(this.leases.selectedLease.key_dates, ['id', this.selectedKeyDateId]);
                keyDate._method = this.selectedKeyDateId === "new" ? "POST" : "PATCH";
                return keyDate;
            }
        },

        save() {
            this.loading = true;
            this.errors = {};

            axios.post(this.getSaveUrl(), this.getSaveData())
                .then((response) => {
                    this.sites.selectedSite = response.data.site || this.sites.selectedSite;
                    this.leases.selectedLease = response.data.lease || this.leases.selectedLease;

                    if (response.data.expense) {
                        this.leases.selectedLease = response.data.expensable;
                        _.remove(this.leases.selectedLease.expenses, expense => expense.id === this.selectedExpenseId);
                        this.leases.selectedLease.expenses.push(response.data.expense);
                        this.selectedExpenseId = response.data.expense ? response.data.expense.id : this.selectedExpenseId;
                    }

                    if (response.data.keyDate) {
                        this.selectedKeyDateId = response.data.keyDate ? response.data.keyDate.id : this.selectedKeyDateId;
                        this.leases.selectedLease.key_dates = response.data.keyDates;
                    }

                    this.$emit('leaseSaved', response.data.lease ? response.data.lease.id : this.selectedLeaseId);
                    this.$eventHub.$emit('leaseSaved');
                    this.$eventHub.$emit('reloadSite');

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });
                })
                .catch((error) => {
                    this.errors = error.response?.data?.errors || {};

                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response?.data?.message || 'Failed to save'
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
};
</script>
